/**
 * Google Places API (New) Implementation
 * Uses the NEW REST-based Places API, not the legacy JavaScript API
 * 
 * Documentation: https://developers.google.com/maps/documentation/places/web-service/place-autocomplete
 */

// Load API key from environment variable for security
// Fallback to VITE_GOOGLE_MAPS_API_KEY if Places API key is not set
const PLACES_API_KEY = import.meta.env.VITE_GOOGLE_PLACES_API_KEY || import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

// Enhanced API key validation and logging
if (!PLACES_API_KEY) {
  console.error('⚠️ Google Places API key is not set in environment variables');
  console.error('Please add VITE_GOOGLE_PLACES_API_KEY or VITE_GOOGLE_MAPS_API_KEY to your .env.local file');
  console.error('Location autocomplete features will not work without an API key');
} else {
  // Log API key status (masked for security)
  const keyPreview = PLACES_API_KEY.length > 8 
    ? PLACES_API_KEY.substring(0, 4) + '...' + PLACES_API_KEY.substring(PLACES_API_KEY.length - 4)
    : '***';
  console.log('✅ Google Places API key loaded:', keyPreview);
}

// NEW Places API endpoints
const AUTOCOMPLETE_URL = 'https://places.googleapis.com/v1/places:autocomplete';
const PLACE_DETAILS_URL = 'https://places.googleapis.com/v1/places';

/**
 * New Places API Service
 * Uses REST endpoints instead of deprecated JavaScript API
 */
export class NewPlacesAPIService {
  constructor() {
    this.cache = new Map();
    this.sessionToken = this.generateSessionToken();
  }

  /**
   * Generate a session token for billing optimization
   * Reuse same token for autocomplete + details in a single user session
   */
  generateSessionToken() {
    return `session_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
  }

  /**
   * Autocomplete search using NEW Places API
   * @param {string} input - Search query
   * @param {object} options - Search options
   * @returns {Promise<Array>} - Autocomplete suggestions
   */
  async autocomplete(input, options = {}) {
    if (!PLACES_API_KEY) {
      console.error('Cannot perform autocomplete: API key is missing');
      throw new Error('Google Places API key is not configured. Please check your environment variables.');
    }

    if (!input || input.trim().length < 2) {
      return [];
    }

    const cacheKey = `autocomplete:${input}`;
    const cached = this.cache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < 300000) { // 5 min cache
      return cached.data;
    }

    try {
      const requestBody = {
        input: input.trim(),
        sessionToken: this.sessionToken,
        locationBias: {
          circle: {
            center: {
              latitude: 33.7490,  // Atlanta, GA
              longitude: -84.3880
            },
            radius: 50000.0  // 50km
          }
        },
        // REMOVED: includedPrimaryTypes filter to allow all place types
        // This allows organizations, businesses, and other place types to appear in autocomplete
        // The API will return the most relevant results regardless of type
        // Note: If you need to restrict types, you can add includedPrimaryTypes back,
        // but it's limited to 5 types and will exclude many valid organizations
        languageCode: 'en'
      };

      const response = await fetch(AUTOCOMPLETE_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Goog-Api-Key': PLACES_API_KEY,
          // Autocomplete prediction uses 'types' field
          'X-Goog-FieldMask': 'suggestions.placePrediction.placeId,suggestions.placePrediction.text,suggestions.placePrediction.structuredFormat,suggestions.placePrediction.types'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = `API request failed: ${response.status}`;
        
        // Parse error response for better error messages
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.error?.message) {
            errorMessage = errorData.error.message;
          }
        } catch (e) {
          // If not JSON, use the text as-is
          errorMessage = errorText || errorMessage;
        }
        
        console.error('Places API (New) error:', {
          status: response.status,
          statusText: response.statusText,
          error: errorMessage,
          url: AUTOCOMPLETE_URL
        });
        
        // Provide more specific error messages
        if (response.status === 403) {
          throw new Error('API key is invalid or API is not enabled. Please check Google Cloud Console configuration.');
        } else if (response.status === 400) {
          throw new Error(`Invalid request: ${errorMessage}`);
        } else if (response.status === 429) {
          throw new Error('API rate limit exceeded. Please try again later.');
        } else {
          throw new Error(`API request failed (${response.status}): ${errorMessage}`);
        }
      }

      const data = await response.json();
      
      // Transform to our expected format
      const results = (data.suggestions || []).map(suggestion => {
        const prediction = suggestion.placePrediction;
        if (!prediction) return null;

        return {
          place_id: prediction.placeId,
          description: prediction.text?.text || '',
          structured_formatting: {
            main_text: prediction.structuredFormat?.mainText?.text || '',
            secondary_text: prediction.structuredFormat?.secondaryText?.text || ''
          },
          types: prediction.types || [],
          ...this.categorizePlaceByType(prediction.types || [], prediction.text?.text || '')
        };
      }).filter(Boolean);

      // Cache results
      this.cache.set(cacheKey, {
        data: results,
        timestamp: Date.now()
      });

      return results;

    } catch (error) {
      console.error('Autocomplete error:', {
        message: error.message,
        input: input.substring(0, 50), // Log first 50 chars of input
        stack: error.stack
      });
      
      // Re-throw with enhanced error message if it's a network error
      if (error.message && (error.message.includes('fetch') || error.message.includes('network'))) {
        throw new Error('Network error: Unable to connect to Google Places API. Please check your internet connection.');
      }
      
      throw error;
    }
  }

  /**
   * Autocomplete search for addresses, cities, states, and zip codes
   * Uses NEW Places API without business type filters
   * @param {string} input - Search query
   * @param {object} options - Search options
   * @returns {Promise<Array>} - Autocomplete suggestions for addresses/locations
   */
  async autocompleteAddress(input, options = {}) {
    if (!PLACES_API_KEY) {
      console.error('Cannot perform address autocomplete: API key is missing');
      throw new Error('Google Places API key is not configured. Please check your environment variables.');
    }

    if (!input || input.trim().length < 2) {
      return [];
    }

    const cacheKey = `autocompleteAddress:${input}`;
    const cached = this.cache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < 300000) { // 5 min cache
      return cached.data;
    }

    try {
      const requestBody = {
        input: input.trim(),
        sessionToken: this.sessionToken,
        locationBias: {
          circle: {
            center: {
              latitude: 33.7490,  // Atlanta, GA
              longitude: -84.3880
            },
            radius: 50000.0  // 50km
          }
        },
        // No includedPrimaryTypes filter - allows addresses, cities, states, zip codes, etc.
        languageCode: 'en'
      };

      const response = await fetch(AUTOCOMPLETE_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Goog-Api-Key': PLACES_API_KEY,
          // Autocomplete prediction uses 'types' field
          'X-Goog-FieldMask': 'suggestions.placePrediction.placeId,suggestions.placePrediction.text,suggestions.placePrediction.structuredFormat,suggestions.placePrediction.types'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = `API request failed: ${response.status}`;
        
        // Parse error response for better error messages
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.error?.message) {
            errorMessage = errorData.error.message;
          }
        } catch (e) {
          // If not JSON, use the text as-is
          errorMessage = errorText || errorMessage;
        }
        
        console.error('Places API (New) address autocomplete error:', {
          status: response.status,
          statusText: response.statusText,
          error: errorMessage,
          url: AUTOCOMPLETE_URL
        });
        
        // Provide more specific error messages
        if (response.status === 403) {
          throw new Error('API key is invalid or API is not enabled. Please check Google Cloud Console configuration.');
        } else if (response.status === 400) {
          throw new Error(`Invalid request: ${errorMessage}`);
        } else if (response.status === 429) {
          throw new Error('API rate limit exceeded. Please try again later.');
        } else {
          throw new Error(`API request failed (${response.status}): ${errorMessage}`);
        }
      }

      const data = await response.json();
      
      // Transform to our expected format
      const results = (data.suggestions || []).map(suggestion => {
        const prediction = suggestion.placePrediction;
        if (!prediction) return null;

        return {
          place_id: prediction.placeId,
          description: prediction.text?.text || '',
          structured_formatting: {
            main_text: prediction.structuredFormat?.mainText?.text || '',
            secondary_text: prediction.structuredFormat?.secondaryText?.text || ''
          },
          types: prediction.types || [],
          ...this.categorizePlaceByType(prediction.types || [], prediction.text?.text || '')
        };
      }).filter(Boolean);

      // Cache results
      this.cache.set(cacheKey, {
        data: results,
        timestamp: Date.now()
      });

      return results;

    } catch (error) {
      console.error('Address autocomplete error:', {
        message: error.message,
        input: input.substring(0, 50), // Log first 50 chars of input
        stack: error.stack
      });
      
      // Re-throw with enhanced error message if it's a network error
      if (error.message && (error.message.includes('fetch') || error.message.includes('network'))) {
        throw new Error('Network error: Unable to connect to Google Places API. Please check your internet connection.');
      }
      
      throw error;
    }
  }

  /**
   * Get place details using NEW Places API
   * @param {string} placeId - Google Place ID
   * @returns {Promise<Object>} - Place details
   */
  async getPlaceDetails(placeId) {
    if (!PLACES_API_KEY) {
      console.error('Cannot fetch place details: API key is missing');
      throw new Error('Google Places API key is not configured. Please check your environment variables.');
    }

    if (!placeId) {
      throw new Error('Place ID is required');
    }

    // Use deduplication to prevent multiple simultaneous requests for same place
    const { deduplicateRequest } = await import('@/utils/requestDeduplication');
    const cacheKey = `placeDetails:${placeId}`;
    
    return await deduplicateRequest(cacheKey, async () => {
      // Check internal cache first (shorter TTL)
      const cached = this.cache.get(`details:${placeId}`);
      if (cached && Date.now() - cached.timestamp < 300000) {
        return cached.data;
      }

      try {
        const response = await fetch(`${PLACE_DETAILS_URL}/${placeId}`, {
        method: 'GET',
        headers: {
          'X-Goog-Api-Key': PLACES_API_KEY,
          'X-Goog-FieldMask': 'id,displayName,formattedAddress,location,types,rating,userRatingCount,businessStatus,websiteUri,internationalPhoneNumber,regularOpeningHours'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = `API request failed: ${response.status}`;
        
        // Parse error response for better error messages
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.error?.message) {
            errorMessage = errorData.error.message;
          }
        } catch (e) {
          // If not JSON, use the text as-is
          errorMessage = errorText || errorMessage;
        }
        
        console.error('Place Details error:', {
          status: response.status,
          statusText: response.statusText,
          error: errorMessage,
          placeId: placeId,
          url: `${PLACE_DETAILS_URL}/${placeId}`
        });
        
        // Provide more specific error messages
        if (response.status === 403) {
          throw new Error('API key is invalid or API is not enabled. Please check Google Cloud Console configuration.');
        } else if (response.status === 400) {
          throw new Error(`Invalid request: ${errorMessage}`);
        } else if (response.status === 404) {
          throw new Error('Place not found. Please try a different location.');
        } else if (response.status === 429) {
          throw new Error('API rate limit exceeded. Please try again later.');
        } else {
          throw new Error(`API request failed (${response.status}): ${errorMessage}`);
        }
      }

      const data = await response.json();

      // Transform to our expected format
      const placeDetails = {
        place_id: data.id,
        name: data.displayName?.text || '',
        formatted_address: data.formattedAddress || '',
        geometry: {
          location: {
            lat: data.location?.latitude || 0,
            lng: data.location?.longitude || 0
          }
        },
        types: data.types || [],
        rating: data.rating,
        user_ratings_total: data.userRatingCount,
        business_status: data.businessStatus,
        website: data.websiteUri,
        formatted_phone_number: data.internationalPhoneNumber,
        opening_hours: data.regularOpeningHours,
        ...this.categorizePlaceByType(data.types, data.displayName?.text || '')
      };

      // Cache results
      this.cache.set(`details:${placeId}`, {
        data: placeDetails,
        timestamp: Date.now()
      });

      return placeDetails;

    } catch (error) {
      console.error('Place details error:', {
        message: error.message,
        placeId: placeId,
        stack: error.stack
      });
      
      // Re-throw with enhanced error message if it's a network error
      if (error.message && (error.message.includes('fetch') || error.message.includes('network'))) {
        throw new Error('Network error: Unable to connect to Google Places API. Please check your internet connection.');
      }
      
      throw error;
    }
    }, { useCache: true, cacheTTL: 300000 }); // 5 minute cache for place details
  }

  /**
   * Categorize place by its types
   */
  categorizePlaceByType(types = [], name = '') {
    const nameLower = name.toLowerCase();
    
    // Youth program categories
    if (types.includes('school') || types.includes('primary_school') || types.includes('secondary_school') || types.includes('university')) {
      return { category: 'education', icon: '🏫', label: 'Education', searchType: 'business' };
    }
    if (types.includes('gym') || types.includes('sports_club') || types.includes('sports_complex')) {
      return { category: 'sports', icon: '⚽', label: 'Sports', searchType: 'business' };
    }
    if (types.includes('museum') || types.includes('art_gallery')) {
      return { category: 'arts', icon: '🎨', label: 'Arts & Culture', searchType: 'business' };
    }
    if (types.includes('park') || types.includes('campground')) {
      return { category: 'outdoor', icon: '🌲', label: 'Outdoor', searchType: 'business' };
    }
    if (nameLower.includes('camp') || nameLower.includes('ymca') || nameLower.includes('boys girls club') || nameLower.includes('boys & girls club')) {
      return { category: 'camp', icon: '🏕️', label: 'Camp', searchType: 'business' };
    }
    if (types.includes('community_center') || types.includes('recreation_center')) {
      return { category: 'community', icon: '🏢', label: 'Community', searchType: 'business' };
    }
    
    // Location types
    if (types.includes('locality') || types.includes('administrative_area_level_1') || 
        types.includes('administrative_area_level_2') || types.includes('neighborhood')) {
      return { category: 'location', icon: '📍', label: 'Location', searchType: 'location' };
    }
    
    // Default to business
    return { category: 'business', icon: '🏢', label: 'Business', searchType: 'business' };
  }

  /**
   * Clear cache
   */
  clearCache() {
    this.cache.clear();
    console.log('Places API cache cleared');
  }

  /**
   * Regenerate session token (call this when user completes a search session)
   */
  newSession() {
    this.sessionToken = this.generateSessionToken();
  }
}

// Export singleton instance
export const newPlacesAPI = new NewPlacesAPIService();

// Export class for testing
export default NewPlacesAPIService;
